import { useEffect, useState } from 'react';
import { useCoursesUrlFilters, useReduxState } from 'hooks';
import { filterValuesSeparator } from 'shared-helpers/consts';
import api from 'api';
import { useApiData } from 'lib/api';
import { LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageShowAllLink
} from 'components/page';
import {
  CoursesAdminTile,
  CoursesSortBy,
  CoursesTile,
  CoursesWrapper
} from '.';

import type { ViewProps } from 'types';
import type { GetCoursesRes } from 'shared-types/responses';

type Props = ViewProps & {
  onFilterDataReceived?: (data: any) => void;
};

const CoursesViewAllCourses = ({
  defaultSort = 'alphabetic',
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 12,
  onFilterDataReceived,
  onNoData = null,
  queryParams = '',
  showAllLinkHref = null,
  title,
  titleShowCount = true
}: Props) => {
  const { messages, user } = useReduxState();
  const [progressByCourseId, setProgressByCourseId] = useState({});

  const { currentPage, getUrl, onPageChange, ready } = useCoursesUrlFilters({
    defaultSort,
    queryParams
  });

  const { data, loading, updateData } = useApiData<GetCoursesRes>({
    onDataUpdated: (data) => {
      // filter data is sent to parent
      if (onFilterDataReceived) {
        if (data?.coursesFilterOptions) {
          onFilterDataReceived(data?.coursesFilterOptions);
        } else {
          onFilterDataReceived(null);
        }
      }

      if (!user || data?.numberOfCourses < 1) return;
      updateProgresses(data?.courses);
    },
    onlyIf: ready,
    url: getUrl('/admin/courses', limitPerPage)
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.courses?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  const updateProgresses = async (updatedCourses) => {
    try {
      const courseIds = updatedCourses
        .filter(
          (course) =>
            course.isAddedUser || course.isGroupUser || course.isPathUser
        )
        .map((course) => course.id);

      if (!courseIds.length) {
        setProgressByCourseId({});
        return;
      }

      const response = await api.get(
        `/admin/users/${user.id}/courses` +
          `?courseIds=${courseIds.join(filterValuesSeparator)}` +
          `&limit=${courseIds.length}`
      );

      const newProgresses = {};
      response.data.courses.forEach((course) => {
        newProgresses[course.id] = course.progressData;
      });
      setProgressByCourseId(newProgresses);
    } catch (error) {
      setProgressByCourseId({});
    }
  };

  return loading || !ready ? (
    <LoadingIndicator />
  ) : data?.numberOfCourses > 0 ? (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.commonAllCourses}
        titleCount={titleShowCount ? data?.numberOfCourses : undefined}
      >
        {isHomeView ? null : (
          <CoursesSortBy
            options={[
              { text: messages.commonAlphabetic, value: 'alphabetic' },
              { text: messages.courseLastPublished, value: 'published' }
            ]}
            defaultSort={defaultSort}
          />
        )}
      </PageSectionHeader>

      <CoursesWrapper isHomeView={isHomeView}>
        {data?.courses.map((course) => (
          <div className="course" key={course.id}>
            {course.isOwner || course.isAdmin ? (
              <CoursesAdminTile course={course} updateCourses={updateData} />
            ) : (
              <CoursesTile
                course={course}
                progressData={progressByCourseId[course.id]}
                updateCourses={updateData}
              />
            )}
          </div>
        ))}
      </CoursesWrapper>

      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={currentPage}
          numberOfPages={data?.pages || 1}
          onPageChange={onPageChange}
        />
      ) : showAllLinkHref && data?.numberOfCourses > data?.courses?.length ? (
        <PageShowAllLink
          href={showAllLinkHref}
          numberOfItems={data?.numberOfCourses}
        />
      ) : null}
    </>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.commonAllCourses}
        titleCount={titleShowCount ? 0 : undefined}
      />

      <PageEmptyIndicator
        icon="book"
        title={messages.coursesNoCoursesFound}
        description={messages.coursesNoCoursesFound}
      />
    </>
  );
};

export default CoursesViewAllCourses;
